import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, User, Building2, Lock, CreditCard, Camera, Trash2,
  Save, Eye, EyeOff, CheckCircle2,
} from "lucide-react";
import MyBilling from "./MyBilling";

const TABS = [
  { key: "profile", label: "Perfil", icon: User },
  { key: "company", label: "Empresa", icon: Building2 },
  { key: "security", label: "Segurança", icon: Lock },
  { key: "billing", label: "Assinatura", icon: CreditCard },
];

function Field({ label, value, onChange, placeholder, type = "text", testId, disabled, hint, maxLength }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-black text-slate-600">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        disabled={disabled}
        maxLength={maxLength}
        className="mt-1 w-full px-3 py-2 text-sm border-2 border-slate-200 focus:border-[#002EA6] rounded-sm outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-500"
      />
      {hint && <span className="mt-0.5 text-[11px] text-slate-500 block">{hint}</span>}
    </label>
  );
}

function SectionCard({ title, description, children, footer }) {
  return (
    <div className="border border-slate-200 bg-white">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Seção</div>
        <div className="text-lg font-black tracking-tight text-slate-900">{title}</div>
        {description && <p className="text-xs text-slate-500 mt-1 max-w-xl">{description}</p>}
      </div>
      <div className="p-5 space-y-4">{children}</div>
      {footer && (
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end">
          {footer}
        </div>
      )}
    </div>
  );
}

/* -------------------- Perfil (usuário logado) -------------------- */
function ProfileTab({ data, onSaved }) {
  const [name, setName] = useState(data.user?.name || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const resp = await api.put("/me/account", { name: name.trim() });
      toast.success("Perfil atualizado");
      onSaved(resp.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <SectionCard
      title="Seu perfil"
      description="Como você quer ser identificado dentro do sistema."
      footer={
        <button
          type="button"
          data-testid="profile-save-btn"
          onClick={save}
          disabled={busy || !name.trim()}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Salvar
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nome" value={name} onChange={setName} testId="profile-name" placeholder="Seu nome" maxLength={120} />
        <Field
          label="E-mail"
          value={data.user?.email}
          onChange={() => {}}
          disabled
          hint="Para alterar o e-mail, entre em contato pelo Suporte."
          testId="profile-email"
        />
      </div>
      <div className="text-[11px] text-slate-500 font-mono">
        Perfil: <span className="uppercase font-bold">{data.user?.role}</span>
      </div>
    </SectionCard>
  );
}

/* -------------------- Empresa (dados fiscais + logo) ------------- */
function CompanyTab({ data, onSaved }) {
  const [f, setF] = useState({
    company_name: data.account?.name || "",
    cpf_cnpj: data.account?.cpf_cnpj || "",
    phone: data.account?.phone || "",
    postal_code: data.account?.postal_code || "",
    address: data.account?.address || "",
    address_number: data.account?.address_number || "",
    complement: data.account?.complement || "",
    province: data.account?.province || "",
    city: data.account?.city || "",
    state: data.account?.state || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const [logoBust, setLogoBust] = useState(Date.now());
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);
  const hasLogo = data.account?.has_logo;
  const logoUrl = hasLogo ? `${API_BASE}/me/logo?t=${logoBust}` : null;

  const save = async () => {
    setBusy(true);
    try {
      const resp = await api.put("/me/account", f);
      toast.success("Dados da empresa atualizados");
      onSaved(resp.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const pickLogo = () => fileRef.current?.click();
  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoBust(Date.now());
      toast.success("Logo atualizado");
      // Force refetch of account so has_logo becomes true
      const { data: fresh } = await api.get("/me/account");
      onSaved(fresh);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLogoBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remover o logo atual da empresa?")) return;
    try {
      await api.delete("/me/logo");
      const { data: fresh } = await api.get("/me/account");
      onSaved(fresh);
      toast.success("Logo removido");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Logo da empresa"
        description="Aparece no cabeçalho e em documentos. PNG / JPG / WEBP até 2 MB. Recomendado: 200×200px."
      >
        <div className="flex items-center gap-5">
          <div className="w-24 h-24 border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
            {hasLogo ? (
              <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" data-testid="company-logo-preview" />
            ) : (
              <Camera className="w-8 h-8 text-slate-400" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              className="hidden"
              data-testid="company-logo-input"
            />
            <button
              type="button"
              onClick={pickLogo}
              disabled={logoBusy}
              data-testid="company-logo-upload"
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
            >
              {logoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              {hasLogo ? "Trocar logo" : "Enviar logo"}
            </button>
            {hasLogo && (
              <button
                type="button"
                onClick={removeLogo}
                data-testid="company-logo-remove"
                className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-red-400 hover:text-red-600 flex items-center gap-2"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remover
              </button>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Dados da empresa"
        description="Essas informações são usadas em documentos e integrações. O nome aparece no menu e nas telas."
        footer={
          <button
            type="button"
            data-testid="company-save-btn"
            onClick={save}
            disabled={busy || !f.company_name.trim()}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar alterações
          </button>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Razão social / Nome fantasia" value={f.company_name} onChange={set("company_name")} testId="company-name" />
          <Field label="CNPJ / CPF" value={f.cpf_cnpj} onChange={set("cpf_cnpj")} placeholder="00.000.000/0000-00" testId="company-cnpj" />
          <Field label="Telefone / WhatsApp" value={f.phone} onChange={set("phone")} placeholder="(11) 99999-9999" testId="company-phone" />
          <Field label="CEP" value={f.postal_code} onChange={set("postal_code")} placeholder="00000-000" testId="company-cep" />
          <div className="md:col-span-2">
            <Field label="Endereço" value={f.address} onChange={set("address")} placeholder="Rua, Avenida…" testId="company-address" />
          </div>
          <Field label="Número" value={f.address_number} onChange={set("address_number")} testId="company-number" />
          <Field label="Complemento" value={f.complement} onChange={set("complement")} testId="company-complement" />
          <Field label="Bairro" value={f.province} onChange={set("province")} testId="company-province" />
          <Field label="Cidade" value={f.city} onChange={set("city")} testId="company-city" />
          <Field label="Estado (UF)" value={f.state} onChange={set("state")} placeholder="SP" maxLength={2} testId="company-state" />
        </div>
      </SectionCard>
    </div>
  );
}

/* -------------------- Segurança (troca de senha) ----------------- */
function SecurityTab() {
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next1.length < 8) return toast.error("A senha nova precisa ter pelo menos 8 caracteres");
    if (next1 !== next2) return toast.error("A confirmação não confere");
    if (next1 === current) return toast.error("A nova senha deve ser diferente da atual");
    setBusy(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next1 });
      setDone(true);
      setCurrent(""); setNext1(""); setNext2("");
      toast.success("Senha alterada. Você será redirecionado para o login.");
      setTimeout(() => { window.location.href = "/login"; }, 2500);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <SectionCard title="Senha alterada" description="Estamos te desconectando por segurança. Você será redirecionado ao login em instantes.">
        <div className="flex items-center gap-3 text-emerald-700">
          <CheckCircle2 className="w-6 h-6" />
          <div className="text-sm">Pronto. Use a nova senha para entrar de novo.</div>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Alterar senha"
      description="Requer sua senha atual. Após a troca você será desconectado e precisará entrar de novo."
      footer={
        <button
          type="submit"
          form="change-password-form"
          data-testid="change-password-btn"
          disabled={busy || !current || !next1 || !next2}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
          Alterar senha
        </button>
      }
    >
      <form id="change-password-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Digite sua senha atual e a nova</div>
          <button type="button" onClick={() => setShow(!show)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
            {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {show ? "Ocultar" : "Mostrar"}
          </button>
        </div>
        <Field label="Senha atual" type={show ? "text" : "password"} value={current} onChange={setCurrent} testId="pw-current" />
        <div />
        <Field label="Nova senha" type={show ? "text" : "password"} value={next1} onChange={setNext1} testId="pw-new" hint="Pelo menos 8 caracteres." />
        <Field label="Confirmar nova senha" type={show ? "text" : "password"} value={next2} onChange={setNext2} testId="pw-new2" />
      </form>
    </SectionCard>
  );
}

/* -------------------- Página principal --------------------------- */
export default function MyAccount() {
  // Initial tab respects ``?tab=`` query param so the "Regularizar agora"
  // banner (in TrialBanner) and the 402 axios interceptor can deep-link
  // straight to the billing tab, bypassing the default Profile view.
  const _initialTab = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      const allowed = ["profile", "company", "security", "billing"];
      return allowed.includes(t) ? t : "profile";
    } catch (_e) { return "profile"; }
  })();
  const [tab, setTab] = useState(_initialTab);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/me/account");
      setData(resp.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (busy && !data) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="my-account-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Configurações</div>
        <h1 className="text-3xl font-black tracking-tighter">Minha Conta</h1>
        <p className="text-sm text-slate-600 mt-1">
          Gerencie seus dados pessoais, os dados da empresa, segurança e sua assinatura.
        </p>
      </div>

      <div className="border-b border-slate-200 flex flex-wrap gap-1" data-testid="my-account-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 flex items-center gap-2 transition-colors ${
              tab === key
                ? "border-[#002EA6] text-[#002EA6]"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div>
        {tab === "profile" && <ProfileTab data={data} onSaved={setData} />}
        {tab === "company" && <CompanyTab data={data} onSaved={setData} />}
        {tab === "security" && <SecurityTab />}
        {tab === "billing" && <MyBilling />}
      </div>
    </div>
  );
}
